import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="bg-primary/10 p-4 rounded-full mb-4">
          <SafeHydrate><Calendar className="h-10 w-10 text-primary" /></SafeHydrate>
        </div>
        <h3 className="text-2xl font-bold text-primary mb-2">Aanvraag ontvangen</h3>
        <p className="text-muted-foreground max-w-sm mb-8">
          Bedankt voor uw interesse. We hebben uw bericht ontvangen en nemen zo snel mogelijk contact met u op.
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary/5">
          Nieuw bericht sturen
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5 pt-4">
      <FormField control={control} name="name" rules={{
      required: "Naam is verplicht",
      minLength: {
        value: 2,
        message: "Naam moet minimaal 2 tekens bevatten"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Naam</FormLabel>
            <FormControl>
              <Input placeholder="Uw volledige naam" className="h-12 border-primary/20 focus:border-primary/50 transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <FormField control={control} name="email" rules={{
      required: "E-mail is verplicht",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Voer een geldig e-mailadres in"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">E-mail</FormLabel>
            <FormControl>
              <Input type="email" placeholder="uw@email.nl" className="h-12 border-primary/20 focus:border-primary/50 transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <FormField control={control} name="message" rules={{
      required: "Bericht is verplicht",
      minLength: {
        value: 10,
        message: "Vertel ons iets meer (minimaal 10 tekens)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Hoe kunnen we u helpen?</FormLabel>
            <FormControl>
              <Textarea placeholder="Uw vraag of opmerking..." className="min-h-[120px] border-primary/20 focus:border-primary/50 transition-colors resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Verzenden...
          </> : "Verstuur aanvraag"}
      </Button>
    </Form>;
}
export default function StartUwCirculaireReis() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="24900" id="samenwerken-met-edumedius" ref={ref} className={`dark bg-gradient-to-br from-primary to-chart-2 text-primary-foreground py-20 md:py-32 rounded-3xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {}
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold leading-tight tracking-tight mb-6">
              Samen bouwen aan de toekomst van het onderwijs
            </h2>
            <p className="text-lg md:text-xl text-primary-foreground/90 font-sans max-w-xl leading-relaxed">
              EduMedius staat midden in het onderwijs. Laat ons meedenken in uw capaciteitsvraagstuk en ontdek de kracht van kwaliteit en flexibiliteit.
            </p>
          </div>

          {}
          <div className="flex flex-col gap-4 w-full sm:w-auto lg:max-w-md lg:ml-auto">
            <Dialog>
              <DialogTrigger asChild>
                <Button size="lg" className="w-full bg-white text-primary hover:bg-white/90 animate-pulse font-medium text-lg h-14 shadow-xl border-none cursor-pointer">
                  <SafeHydrate><Calendar className="mr-2 h-5 w-5" /></SafeHydrate>
                  Maak kennis met EduMedius
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-primary">Maak kennis met EduMedius</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Laat uw gegevens achter en we nemen contact met u op voor een vrijblijvend gesprek over uw capaciteitsvraagstuk.
                  </DialogDescription>
                </DialogHeader>
                <FormProvider className="w-full" formId="kennismaken-form-start-reis" sectionName="StartUwCirculaireReis" defaultValues={{
                name: "",
                email: "",
                message: ""
              }} submitText="Verstuur aanvraag">
                  <ContactForm />
                </FormProvider>
              </DialogContent>
            </Dialog>

            <Button asChild variant="outline" size="lg" className="w-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-medium text-lg h-14 transition-colors">
              <Link to="#">
                <FileText className="mr-2 h-5 w-5" />
                Bekijk onze diensten
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}